import React, { useMemo, useState } from 'react';
import {
  Box, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, ToggleButtonGroup,
  ToggleButton, Stack, FormHelperText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from '../../../config/axiosInstance';
import { useSnackbar } from 'notistack';
import useConfirm from '../../calendar/dialogs/UseConfirm';
import { WEEKDAYS, describeRule, isWeekdayCellRule, errorMessage } from './staffingUtils';

const KINDS = [
  { value: 'single', label: 'Día puntual' },
  { value: 'range', label: 'Rango de fechas' },
  { value: 'weekdays', label: 'Solo días de la semana' },
];

const EMPTY_FORM = {
  workshiftId: '', kind: 'single', dateFrom: '', dateTo: '', weekdays: [], total: '', reason: '',
};

const kindOf = (rule) => {
  if (!rule.date_from && !rule.date_to) return 'weekdays';
  if (rule.date_from && rule.date_from === rule.date_to) return 'single';
  return 'range';
};

// Excepciones al valor por defecto: día puntual, rango de fechas (opcionalmente solo
// ciertos días de la semana) o días de la semana sin límite de fechas.
export default function StaffingExceptions({ stationId, area, workshifts, rules, canWrite, onChanged }) {
  const { enqueueSnackbar } = useSnackbar();
  const { confirm, ConfirmDialog } = useConfirm();
  const primaryWorkareaId = area.workareaIds[0];

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const workshiftName = (id) => workshifts.find((w) => w.id === id)?.name || `Turno ${id}`;

  const exceptions = useMemo(
    () => (rules || [])
      .filter((r) => area.workareaIds.includes(r.workarea_id) && !isWeekdayCellRule(r))
      .sort((a, b) => String(a.date_from || '9999').localeCompare(String(b.date_from || '9999')) || a.id - b.id),
    [rules, area]
  );

  const setField = (field) => (event) => setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, workshiftId: workshifts[0]?.id ?? '' });
    setOpen(true);
  };

  const openEdit = (rule) => {
    setEditing(rule);
    setForm({
      workshiftId: rule.workshift_id,
      kind: kindOf(rule),
      dateFrom: rule.date_from || '',
      dateTo: rule.date_to || '',
      weekdays: rule.weekdays || [],
      total: String(rule.total),
      reason: rule.reason || '',
    });
    setOpen(true);
  };

  const payloadFromForm = () => {
    const { kind, dateFrom, dateTo, weekdays } = form;
    return {
      total: form.total === '' ? null : Number(form.total),
      reason: form.reason.trim() || null,
      date_from: kind === 'weekdays' ? null : dateFrom || null,
      date_to: kind === 'single' ? dateFrom || null : kind === 'range' ? dateTo || null : null,
      weekdays: kind === 'single' ? null : (weekdays.length ? weekdays : null),
    };
  };

  const formError = () => {
    if (!form.workshiftId) return 'Elegí un turno.';
    if (form.total === '') return 'Indicá la cantidad.';
    if (form.kind === 'single' && !form.dateFrom) return 'Elegí la fecha.';
    if (form.kind === 'range' && (!form.dateFrom || !form.dateTo)) return 'Elegí la fecha desde y hasta.';
    if (form.kind === 'range' && form.dateFrom > form.dateTo) return 'La fecha desde no puede ser posterior a la fecha hasta.';
    if (form.kind === 'weekdays' && form.weekdays.length === 0) return 'Elegí al menos un día de la semana.';
    return null;
  };

  const handleSave = async () => {
    const problem = formError();
    if (problem) { enqueueSnackbar(problem, { variant: 'warning' }); return; }
    setSaving(true);
    try {
      const body = payloadFromForm();
      let response;
      if (editing) {
        response = await axios.put(`/station/${stationId}/staffing/rules/${editing.id}`, body);
      } else {
        response = await axios.post(`/station/${stationId}/staffing/rules`, {
          ...body, workarea_id: primaryWorkareaId, workshift_id: Number(form.workshiftId),
        });
      }
      setOpen(false);
      enqueueSnackbar('Excepción guardada correctamente.', { variant: 'success' });
      await onChanged(response?.data?.suggested_recalc);
    } catch (e) {
      enqueueSnackbar(errorMessage(e, 'No se pudo guardar la excepción.'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rule) => {
    const ok = await confirm({
      title: 'Eliminar excepción',
      content: `¿Seguro que querés eliminar la excepción de "${workshiftName(rule.workshift_id)}" (${describeRule(rule)})? Esos días vuelven a usar el valor por defecto.`,
      confirmText: 'Eliminar',
    });
    if (!ok) return;
    try {
      const response = await axios.delete(`/station/${stationId}/staffing/rules/${rule.id}`);
      enqueueSnackbar('Excepción eliminada.', { variant: 'success' });
      await onChanged(response?.data?.suggested_recalc);
    } catch (e) {
      enqueueSnackbar(errorMessage(e, 'No se pudo eliminar la excepción.'), { variant: 'error' });
    }
  };

  const toggleWeekdays = (_e, value) => setForm((prev) => ({ ...prev, weekdays: value }));

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Cambios puntuales o por período que reemplazan al valor por defecto y al del día de la semana.
        </Typography>
        {canWrite && (
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreate} disabled={workshifts.length === 0}>
            Nueva excepción
          </Button>
        )}
      </Stack>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Turno</TableCell>
            <TableCell>Cuándo</TableCell>
            <TableCell align="center">Cantidad</TableCell>
            <TableCell>Motivo</TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {exceptions.map((rule) => (
            <TableRow key={rule.id}>
              <TableCell>{workshiftName(rule.workshift_id)}</TableCell>
              <TableCell>{describeRule(rule)}</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>{rule.total}</TableCell>
              <TableCell sx={{ color: rule.reason ? 'inherit' : 'text.disabled' }}>{rule.reason || '—'}</TableCell>
              <TableCell align="right">
                {canWrite && (
                  <>
                    <IconButton size="small" onClick={() => openEdit(rule)} aria-label="Editar excepción"><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(rule)} aria-label="Eliminar excepción"><DeleteIcon fontSize="small" /></IconButton>
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
          {exceptions.length === 0 && (
            <TableRow><TableCell colSpan={5}>No hay excepciones cargadas para esta área.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>

      {ConfirmDialog}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Editar excepción' : 'Nueva excepción'} · {area.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select label="Turno" value={form.workshiftId} onChange={setField('workshiftId')}
              disabled={Boolean(editing)} fullWidth size="small"
            >
              {workshifts.map((w) => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
            </TextField>

            <Box>
              <ToggleButtonGroup
                exclusive size="small" value={form.kind} fullWidth
                onChange={(_e, value) => value && setForm((prev) => ({ ...prev, kind: value }))}
              >
                {KINDS.map((k) => <ToggleButton key={k.value} value={k.value}>{k.label}</ToggleButton>)}
              </ToggleButtonGroup>
            </Box>

            {form.kind === 'single' && (
              <TextField type="date" label="Fecha" value={form.dateFrom} onChange={setField('dateFrom')}
                InputLabelProps={{ shrink: true }} size="small" fullWidth />
            )}

            {form.kind === 'range' && (
              <Stack direction="row" spacing={2}>
                <TextField type="date" label="Desde" value={form.dateFrom} onChange={setField('dateFrom')}
                  InputLabelProps={{ shrink: true }} size="small" fullWidth />
                <TextField type="date" label="Hasta" value={form.dateTo} onChange={setField('dateTo')}
                  InputLabelProps={{ shrink: true }} size="small" fullWidth />
              </Stack>
            )}

            {form.kind !== 'single' && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {form.kind === 'range' ? 'Solo estos días de la semana (opcional)' : 'Días de la semana'}
                </Typography>
                <ToggleButtonGroup value={form.weekdays} onChange={toggleWeekdays} size="small" sx={{ display: 'flex', flexWrap: 'wrap' }}>
                  {WEEKDAYS.map((d) => <ToggleButton key={d.value} value={d.value} sx={{ px: 1.5 }}>{d.short}</ToggleButton>)}
                </ToggleButtonGroup>
                {form.kind === 'range' && (
                  <FormHelperText>Si no se elige ninguno, aplica a todos los días del rango.</FormHelperText>
                )}
                {form.kind === 'weekdays' && (
                  <FormHelperText>Sin límite de fechas: aplica a esos días de la semana siempre.</FormHelperText>
                )}
              </Box>
            )}

            <TextField
              type="number" label="Cantidad" value={form.total} size="small"
              onChange={(e) => setForm((prev) => ({ ...prev, total: e.target.value.replace(/[^\d]/g, '') }))}
              inputProps={{ min: 0 }} helperText="Cantidad de personas necesarias en el turno. Si es 0, el turno no se exige."
            />

            <TextField label="Motivo (opcional)" value={form.reason} onChange={setField('reason')}
              size="small" fullWidth inputProps={{ maxLength: 255 }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
